import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FingerScan, Mobile } from 'iconsax-react';
import logo from '../../../../public/assets/logo.png';
import AdminSecurityApi from '@infrastructure/admin-security-api';
import { createPasskey, passkeysSupported } from '@application/common/helpers/webauthn';
import { useAppDispatch } from '@application/store/useStore';
import { adminSessionStarted } from '@application/features/auth/authSlice';
import { black400, black500 } from '../../constants/color';
import { centered, loginContainer } from '../freelancer/account/style';
import { PasswordField, RecoveryCodes, securityMessage, TotpSetup } from '../admin/settings/access/SecurityParts';
const primary = { minHeight: 48, borderRadius: '30px', textTransform: 'none', fontWeight: 600 };
// Public page opened from the Rebels invitation e-mail (add-rebels-admin-security-and-mcp-connectors, task R4.2).
// The token is read from the URL fragment (never sent in a request URL, never logged) and removed from the address bar
// immediately. Port of the QPortal live frontend in the Rebels sign-in card.
export const AdminInvitationActivation = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [token] = useState(() => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') || '');
    const [invitation, setInvitation] = useState(null);
    const [step, setStep] = useState('loading');
    const [password, setPassword] = useState('');
    const [repeat, setRepeat] = useState('');
    const [enrollment, setEnrollment] = useState(null);
    const [codes, setCodes] = useState([]);
    const [sessionToken, setSessionToken] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    useEffect(() => {
        if (window.location.hash)
            window.history.replaceState(null, '', window.location.pathname);
        if (!token) {
            setStep('invalid');
            return;
        }
        AdminSecurityApi.viewInvitation(token).then((view) => { setInvitation(view); setStep('choose'); }).catch(() => setStep('invalid'));
    }, [token]);
    const enter = (accessToken) => {
        dispatch(adminSessionStarted(accessToken));
        navigate('/settings?tab=security', { replace: true });
    };
    const withPasskey = async () => {
        setBusy(true);
        setError('');
        try {
            const { challengeId, options } = await AdminSecurityApi.invitationPasskeyOptions(token);
            const credentialJson = await createPasskey(options);
            enter((await AdminSecurityApi.invitationPasskeyComplete(token, challengeId, credentialJson)).accessToken);
        }
        catch (e) {
            setError(securityMessage(e));
        }
        finally {
            setBusy(false);
        }
    };
    const beginTotp = async (event) => {
        event.preventDefault();
        if (password !== repeat) {
            setError('The passwords do not match.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            setEnrollment(await AdminSecurityApi.invitationTotpBegin(token, password));
            setStep('totp');
        }
        catch (e) {
            setError(securityMessage(e));
        }
        finally {
            setBusy(false);
        }
    };
    const confirmTotp = async (code) => {
        if (!enrollment)
            return;
        setBusy(true);
        setError('');
        try {
            const result = await AdminSecurityApi.invitationTotpComplete(token, enrollment.challengeId, code);
            setPassword('');
            setRepeat('');
            setSessionToken(result.accessToken);
            setCodes(result.recoveryCodes || []);
            setStep('codes');
        }
        catch (e) {
            setError(securityMessage(e));
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx(Box, { sx: { ...centered, minHeight: '100vh', py: 4, boxSizing: 'border-box' }, children: _jsxs(Box, { sx: { ...loginContainer, width: 'min(520px, calc(100vw - 32px))' }, children: [_jsx("img", { src: logo, alt: 'Rebels', style: { width: 150, height: 'auto' } }), step === 'loading' && _jsx(Box, { py: 6, textAlign: 'center', children: _jsx(CircularProgress, { "aria-label": 'Checking your invitation' }) }), step === 'invalid' && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'h5', mt: 3, color: black500, children: "This link is no longer valid" }), _jsx(Typography, { variant: 'body2', color: black400, mt: 1, children: "Invitation links work once and expire after 48 hours. Ask your SuperAdmin to send a new invitation." }), _jsx(Button, { sx: { mt: 3 }, onClick: () => navigate('/login'), children: "Go to sign in" })] })), invitation && step !== 'invalid' && step !== 'loading' && (_jsxs(Box, { sx: { width: '100%' }, children: [_jsx(Typography, { fontSize: '30px', fontWeight: 500, mt: 3, color: '#000000', sx: { overflowWrap: 'anywhere' }, children: invitation.kind === 'Reenrollment' ? 'Set up your sign-in again' : `Welcome, ${invitation.firstName}` }), _jsxs(Typography, { variant: 'body2', color: black400, mt: 1, sx: { overflowWrap: 'anywhere' }, children: ["Activate admin access to the Rebels portal for ", _jsx("strong", { children: invitation.email }), ". This link is valid until", ' ', new Date(invitation.expiresUtc.endsWith('Z') ? invitation.expiresUtc : invitation.expiresUtc + 'Z').toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }), "."] })] })), step === 'choose' && (_jsxs(Stack, { gap: 1.5, mt: 3, sx: { width: '100%' }, children: [passkeysSupported() ? (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'contained', startIcon: _jsx(FingerScan, { size: 20 }), sx: primary, disabled: busy, onClick: withPasskey, children: busy ? 'Waiting for your passkey…' : 'Create a passkey (recommended)' }), _jsx(Typography, { variant: 'caption', color: black400, children: "Uses Face ID, Touch ID, Windows Hello, your phone or a security key. No password needed." })] })) : (_jsx(Alert, { severity: 'info', children: "This browser does not support passkeys. Use an authenticator app instead." })), _jsx(Button, { variant: 'outlined', color: 'secondary', startIcon: _jsx(Mobile, { size: 20 }), sx: primary, disabled: busy, onClick: () => { setError(''); setStep('password'); }, children: "Use password + authenticator app" })] })), step === 'password' && (_jsxs(Box, { component: 'form', onSubmit: beginTotp, sx: { width: '100%', mt: 2 }, noValidate: true, children: [_jsx(Typography, { variant: 'body2', color: black400, children: "Choose a password of at least 12 characters. A short sentence is easy to remember and hard to guess." }), _jsx(PasswordField, { value: password, onChange: setPassword, label: 'New password', autoComplete: 'new-password' }), _jsx(PasswordField, { value: repeat, onChange: setRepeat, label: 'Repeat password', autoComplete: 'new-password' }), _jsx(Button, { type: 'submit', fullWidth: true, variant: 'contained', sx: { ...primary, mt: 2 }, disabled: busy || password.length < 12 || !repeat, children: busy ? 'Checking…' : 'Next' }), _jsx(Button, { sx: { mt: 1 }, onClick: () => setStep('choose'), disabled: busy, children: "Back" })] })), step === 'totp' && enrollment && (_jsx(Box, { sx: { width: '100%', mt: 2 }, children: _jsx(TotpSetup, { secret: enrollment.secret, provisioningUri: enrollment.provisioningUri, busy: busy, error: error, onConfirm: confirmTotp }) })), step === 'codes' && (_jsx(Box, { sx: { width: '100%', mt: 2 }, children: _jsx(RecoveryCodes, { codes: codes, onDone: () => enter(sessionToken), doneLabel: 'Go to the portal' }) })), error && step !== 'totp' && _jsx(Alert, { severity: 'error', sx: { mt: 2, width: '100%' }, children: error })] }) }));
};
